import * as yup from 'yup';
import { lazy } from 'yup';
import type { Network } from '../../../types/shared';
import checkMetricWithLevel from '../utils/checkMetricWithLevel';
import type { LevelValue } from '../types';
import { SINGLE_ACTIONS } from '../constants';

const subActionWithValue = yup
  .object()
  .shape({
    action: yup.string().default('').notRequired(),
    value: yup.string().when('action', {
      is: (action: string) => !!action,
      then: () => yup.string().default('').required('Sub-action value is a required field'),
      otherwise: () => yup.string().default('').notRequired(),
    }),
  })
  .defined();

const subActionWithoutValue = yup
  .object()
  .shape({
    action: yup.string().default('').notRequired(),
  })
  .defined();

const action = yup.object().shape({
  action: yup.string().required('Action is a required field'),
  value: yup.string().when('action', {
    is: (action: string) => !!action && !SINGLE_ACTIONS.includes(action),
    then: () => yup.string().default('').required('Action value is a required field'),
    otherwise: () => yup.string().default('').notRequired(),
  }),
  sub_actions: yup.array().when('action', {
    is: (action: string) => action.includes('duplicate'),
    then: () => yup.array().of(subActionWithValue),
    otherwise: () => yup.array().of(subActionWithoutValue),
  }),
});

export const actionArraySchema = yup.array().of(action);

const DEPENDANT_METRICS = ['constant', 'flow'];

const type = yup.string().oneOf(['condition']).required('Type is a required field');
const operator = yup.string().required('Operator is a required field').default('');
const metric = (prefix: string) =>
  yup
    .string()
    .default('')
    .required(`${prefix.includes('second') ? 'Second metric' : 'Metric'} is a required field`)
    .test('valid-metric-and-level', function (value: string) {
      const { context } = this.options;

      if (!checkMetricWithLevel({ value, level: String(context?.ruleLevel) as LevelValue })) {
        return this.createError({
          message: `Invalid metric "${value}", when rule level set to "${context?.ruleLevel}"`,
        });
      }

      return true;
    });

const period = (key: string) =>
  yup
    .string()
    .default('')
    .when(key, {
      is: (metric: string) => DEPENDANT_METRICS.includes(metric),
      then: (schema) => schema.strip(),
      otherwise: (schema) => schema.required('Period is a required field'),
    });

const googleMetricLevel = yup.string().default('');
const facebookMetricLevel = (key: string) =>
  yup
    .string()
    .default('')
    .when(key, {
      is: (metric: string) => DEPENDANT_METRICS.includes(metric),
      then: (schema) => schema.strip(),
      otherwise: (schema) => schema.required(),
    });

const metric_multiplier = yup.number();

const metric_level = (key: string) =>
  yup.lazy((value, options) => {
    const network = options.context?.network as Network;
    if (network === 'facebook') {
      return facebookMetricLevel(key);
    }

    if (network === 'google') {
      return googleMetricLevel;
    }

    return yup.string().test('alwaysError', function () {
      return this.createError({ message: 'Invalid network' });
    });
  });
const constant_name = (key: string) =>
  yup
    .string()
    .default('')
    .when(key, {
      is: (metric: string) => DEPENDANT_METRICS.includes(metric),
      then: (schema) => schema.required('Constant name is a required field'),
      otherwise: (schema) => schema.strip(),
    });

const addPrefix = (prefix: string, key: string) => prefix + key;

const createConditionSchemaObj = (conditionType: 'first' | 'second') => {
  const isSingle = conditionType === 'first';
  const prefix = isSingle ? '' : 'second_';

  const critical_value = yup
    .string()
    .default('')
    .when('$critical_value', {
      is: () => isSingle,
      then: (schema) => schema.required('Critical value is a required field'),
      otherwise: (schema) => schema.strip(),
    });

  const metricKey = addPrefix(prefix, 'metric');

  return {
    type,
    operator,
    critical_value,
    [addPrefix(prefix, 'metric_multiplier')]: metric_multiplier,
    [addPrefix(prefix, 'metric')]: metric(prefix),
    [addPrefix(prefix, 'period')]: period(metricKey),
    [addPrefix(prefix, 'metric_level')]: metric_level(metricKey),
    [addPrefix(prefix, !prefix ? 'constant_name' : 'metric_constant_name')]: constant_name(metricKey),
  };
};

const singleConditionItemSchema = yup
  .object()
  .shape({
    ...createConditionSchemaObj('first'),
  })
  .noUnknown();
const multipleConditionItemSchema = yup
  .object()
  .shape({
    ...createConditionSchemaObj('first'),
    ...createConditionSchemaObj('second'),
  })
  .noUnknown();

export const conditionsArraySchema = yup.array().of(
  lazy((item: Record<string, string | number>) => {
    const isMultipleCondition = Object.keys(item).some((key) => key.includes('second'));

    return isMultipleCondition ? multipleConditionItemSchema : singleConditionItemSchema;
  })
);

const conditionSchema = yup.object().shape({
  type: yup.string().required().oneOf(['group']),
  operator: yup.string().required().oneOf(['AND', 'OR']),
  conditions: conditionsArraySchema,
});

const taskItemSchema = yup.object().shape({
  condition: conditionSchema,
  actions: actionArraySchema,
});

export const tasksArraySchema = yup.array().of(taskItemSchema);
